import React, { useState } from 'react';
import { motion } from 'framer-motion';

// ============================================
// Mock Data - Completely Static, No Database
// ============================================
const mockStats = {
  totalRevenue: 847500000,
  totalExpenses: 234200000,
  netIncome: 613300000,
  totalUnits: 24,
  occupiedUnits: 21,
  vacantUnits: 3,
  occupancyRate: 87.5,
  contractsSigned: 18,
  contractsPending: 3,
  overduePayments: 2,
};

const mockAlerts = {
  expiringLeases: [
    { id: '1', unitName: 'Villa Seminyak 3A', tenantName: 'John Smith', daysLeft: 14 },
    { id: '2', unitName: 'Apartment Canggu B2', tenantName: 'Sarah Johnson', daysLeft: 21 },
  ],
  noticeDeadlines: [
    { id: '3', unitName: 'Villa Ubud 7', tenantName: 'Michael Chen', daysLeft: 7 },
  ],
  overdueInvoices: [
    { id: '4', invoiceNumber: 'INV-2024-0087', unitName: 'Villa Seminyak 5B', amount: 45000000 },
    { id: '5', invoiceNumber: 'INV-2024-0092', unitName: 'Apartment Kuta A1', amount: 28500000 },
  ],
};

const mockWorkOrders = [
  { id: '1', title: 'AC Repair - Villa Seminyak 3A', status: 'In Progress', priority: 'High', assignee: 'Made Wirawan' },
  { id: '2', title: 'Pool Cleaning - Villa Ubud 7', status: 'Pending', priority: 'Medium', assignee: 'Ketut Sari' },
  { id: '3', title: 'Plumbing Fix - Apartment Canggu B2', status: 'Completed', priority: 'High', assignee: 'Wayan Gede' },
  { id: '4', title: 'Garden Maintenance - Villa Seminyak 5B', status: 'Pending', priority: 'Low', assignee: 'Nyoman Adi' },
  { id: '5', title: 'Electrical Check - Apartment Kuta A1', status: 'In Progress', priority: 'Medium', assignee: 'Made Wirawan' },
];

const mockTenants = [
  { id: '1', name: 'John Smith', unit: 'Villa Seminyak 3A', lease: 'Dec 2024 - Dec 2025', status: 'Active', rent: 45000000 },
  { id: '2', name: 'Sarah Johnson', unit: 'Apartment Canggu B2', lease: 'Jan 2025 - Jan 2026', status: 'Active', rent: 28000000 },
  { id: '3', name: 'Michael Chen', unit: 'Villa Ubud 7', lease: 'Mar 2024 - Mar 2025', status: 'Notice', rent: 52000000 },
  { id: '4', name: 'Emma Wilson', unit: 'Villa Seminyak 5B', lease: 'Jun 2024 - Jun 2025', status: 'Active', rent: 48000000 },
];

const mockExpenses = [
  { id: '1', description: 'Pool Maintenance Supplies', category: 'Maintenance', amount: 2500000, date: '2024-02-15' },
  { id: '2', description: 'Security Service - Feb', category: 'Services', amount: 8000000, date: '2024-02-01' },
  { id: '3', description: 'Gardening Equipment', category: 'Equipment', amount: 3200000, date: '2024-02-12' },
  { id: '4', description: 'Utility Bills - Villa Complex', category: 'Utilities', amount: 15000000, date: '2024-02-10' },
];

const mockProperties = [
  { id: '1', name: 'Villa Seminyak 3A', type: 'Villa', status: 'Occupied', rent: 45000000, tenant: 'John Smith' },
  { id: '2', name: 'Apartment Canggu B2', type: 'Apartment', status: 'Occupied', rent: 28000000, tenant: 'Sarah Johnson' },
  { id: '3', name: 'Villa Ubud 7', type: 'Villa', status: 'Notice', rent: 52000000, tenant: 'Michael Chen' },
  { id: '4', name: 'Villa Seminyak 5B', type: 'Villa', status: 'Occupied', rent: 48000000, tenant: 'Emma Wilson' },
  { id: '5', name: 'Apartment Kuta A1', type: 'Apartment', status: 'Vacant', rent: 25000000, tenant: null },
];

// Daily Pulse Analytics
const mockPulseData = {
  occupancyTrend: [85, 87, 86, 88, 87, 89, 87.5],
  revenueByMonth: [
    { month: 'Sep', revenue: 680000000 },
    { month: 'Oct', revenue: 720000000 },
    { month: 'Nov', revenue: 790000000 },
    { month: 'Dec', revenue: 850000000 },
    { month: 'Jan', revenue: 820000000 },
    { month: 'Feb', revenue: 847500000 },
  ],
  maintenanceByCategory: [
    { category: 'Plumbing', count: 12, cost: 45000000 },
    { category: 'Electrical', count: 8, cost: 32000000 },
    { category: 'AC/HVAC', count: 15, cost: 67000000 },
    { category: 'Pool', count: 6, cost: 28000000 },
    { category: 'Garden', count: 10, cost: 18000000 },
  ],
  todayMetrics: {
    checkIns: 3,
    checkOuts: 2,
    workOrdersCreated: 4,
    workOrdersCompleted: 6,
    paymentsReceived: 125000000,
    issuesReported: 2,
  },
};

// Inventory Items
const mockInventory = [
  { id: '1', name: 'Samsung Split AC 2PK', category: 'AC/HVAC', location: 'Villa Seminyak 3A', status: 'Good', lastService: '2024-12-15', value: 8500000 },
  { id: '2', name: 'LG Refrigerator', category: 'Appliance', location: 'Apartment Canggu B2', status: 'Good', lastService: '2024-11-20', value: 12000000 },
  { id: '3', name: 'Pool Pump Pentair', category: 'Pool Equipment', location: 'Villa Ubud 7', status: 'Needs Service', lastService: '2024-08-10', value: 15000000 },
  { id: '4', name: 'Water Heater Ariston', category: 'Plumbing', location: 'Villa Seminyak 5B', status: 'Good', lastService: '2024-10-05', value: 4500000 },
  { id: '5', name: 'Generator 5KVA', category: 'Electrical', location: 'Villa Seminyak 3A', status: 'Good', lastService: '2024-09-22', value: 25000000 },
  { id: '6', name: 'CCTV System 8CH', category: 'Security', location: 'Apartment Kuta A1', status: 'Good', lastService: '2024-12-01', value: 8000000 },
  { id: '7', name: 'Washing Machine LG', category: 'Appliance', location: 'Villa Ubud 7', status: 'Repair Scheduled', lastService: '2024-07-15', value: 6500000 },
];

const mockInventoryStats = {
  totalItems: 156,
  totalValue: 2450000000,
  needsService: 12,
  inRepair: 4,
};

// Vendors
const mockVendors = [
  { id: '1', name: 'Bali Pool Services', category: 'Pool Maintenance', contact: '+62 812 3456 7890', rating: 4.8, jobsCompleted: 45, status: 'Active' },
  { id: '2', name: 'Gede Electric', category: 'Electrical', contact: '+62 813 4567 8901', rating: 4.6, jobsCompleted: 32, status: 'Active' },
  { id: '3', name: 'Cool Air Bali', category: 'AC/HVAC', contact: '+62 814 5678 9012', rating: 4.9, jobsCompleted: 58, status: 'Active' },
  { id: '4', name: 'Green Garden Bali', category: 'Landscaping', contact: '+62 815 6789 0123', rating: 4.5, jobsCompleted: 120, status: 'Active' },
  { id: '5', name: 'Pak Made Plumbing', category: 'Plumbing', contact: '+62 816 7890 1234', rating: 4.7, jobsCompleted: 67, status: 'Active' },
  { id: '6', name: 'Bali Security Pro', category: 'Security', contact: '+62 817 8901 2345', rating: 4.4, jobsCompleted: 24, status: 'On Hold' },
];

// Schedule/Calendar Events
const mockScheduleEvents = [
  { id: '1', title: 'AC Maintenance - Villa Seminyak 3A', date: '2024-02-18', time: '09:00', type: 'maintenance', vendor: 'Cool Air Bali' },
  { id: '2', title: 'Tenant Move-in - Apartment Kuta A1', date: '2024-02-18', time: '14:00', type: 'checkin', tenant: 'New Tenant' },
  { id: '3', title: 'Pool Inspection - Villa Ubud 7', date: '2024-02-19', time: '10:00', type: 'inspection', vendor: 'Bali Pool Services' },
  { id: '4', title: 'Lease Renewal Meeting', date: '2024-02-19', time: '15:00', type: 'meeting', tenant: 'John Smith' },
  { id: '5', title: 'Quarterly Property Inspection', date: '2024-02-20', time: '09:00', type: 'inspection', property: 'All Seminyak' },
  { id: '6', title: 'Garden Service - Villa Complex', date: '2024-02-20', time: '08:00', type: 'maintenance', vendor: 'Green Garden Bali' },
  { id: '7', title: 'Tenant Check-out - Villa Ubud 7', date: '2024-02-21', time: '11:00', type: 'checkout', tenant: 'Michael Chen' },
];

// Activity Log
const mockActivityLog = [
  { id: '1', action: 'Work order completed', description: 'Plumbing Fix - Apartment Canggu B2', user: 'Wayan Gede', timestamp: '2024-02-17 14:32', type: 'maintenance' },
  { id: '2', action: 'Payment received', description: 'INV-2024-0098 - Villa Seminyak 3A', user: 'System', timestamp: '2024-02-17 13:15', type: 'payment' },
  { id: '3', action: 'New work order', description: 'AC Repair - Villa Seminyak 3A', user: 'John Doe', timestamp: '2024-02-17 11:45', type: 'maintenance' },
  { id: '4', action: 'Tenant notice submitted', description: 'Villa Ubud 7 - Michael Chen', user: 'System', timestamp: '2024-02-17 10:20', type: 'tenant' },
  { id: '5', action: 'Expense recorded', description: 'Pool Maintenance Supplies', user: 'John Doe', timestamp: '2024-02-17 09:30', type: 'expense' },
  { id: '6', action: 'Vendor assigned', description: 'Cool Air Bali - AC Repair', user: 'John Doe', timestamp: '2024-02-16 16:45', type: 'vendor' },
  { id: '7', action: 'Lease signed', description: 'Apartment Canggu B2 - Sarah Johnson', user: 'System', timestamp: '2024-02-16 14:00', type: 'tenant' },
  { id: '8', action: 'Inventory updated', description: 'New Generator added - Villa Seminyak 3A', user: 'John Doe', timestamp: '2024-02-16 11:20', type: 'inventory' },
];

type PageType = 'dashboard' | 'maintenance' | 'expenses' | 'pulse' | 'properties' | 'tenants' | 'inventory' | 'vendors' | 'map' | 'schedule' | 'activity';

interface NavItem {
  name: string;
  icon: string;
  page: PageType;
  badge?: number;
}

interface NavSection {
  section: string;
  items: NavItem[];
}

const mockNavigation: NavSection[] = [
  { section: 'Operations', items: [
    { name: 'Maintenance', icon: 'maintenance', page: 'maintenance', badge: 5 },
    { name: 'Expenses', icon: 'expenses', page: 'expenses' },
    { name: 'Daily Pulse', icon: 'pulse', page: 'pulse' },
  ]},
  { section: 'Management', items: [
    { name: 'Properties', icon: 'properties', page: 'properties' },
    { name: 'Tenants', icon: 'tenants', page: 'tenants' },
    { name: 'Inventory', icon: 'inventory', page: 'inventory' },
    { name: 'Vendors', icon: 'vendors', page: 'vendors' },
  ]},
  { section: 'Tools', items: [
    { name: 'Map', icon: 'map', page: 'map' },
    { name: 'Schedule', icon: 'schedule', page: 'schedule' },
    { name: 'Activity', icon: 'activity', page: 'activity' },
  ]},
];

// Format IDR currency
const formatIDR = (amount: number) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// ============================================
// Icon Components
// ============================================
const icons: Record<string, React.ReactNode> = {
  maintenance: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  expenses: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  pulse: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  properties: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
  tenants: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  inventory: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  ),
  vendors: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  map: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
    </svg>
  ),
  schedule: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  activity: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  ),
  bell: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  ),
  home: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  ),
};

// ============================================
// Demo Sidebar Component
// ============================================
interface DemoSidebarProps {
  activePage: PageType;
  onPageChange: (page: PageType) => void;
}

const DemoSidebar: React.FC<DemoSidebarProps> = ({ activePage, onPageChange }) => {
  return (
    <div className="w-56 bg-[#0d1117] border-r border-[#21262d] flex flex-col h-full">
      {/* Logo */}
      <div className="p-4 border-b border-[#21262d]">
        <button
          onClick={() => onPageChange('dashboard')}
          className="flex items-center gap-3 w-full hover:opacity-80 transition-opacity"
        >
          <div className="w-9 h-9 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center">
            <span className="text-[#0d1117] text-lg font-bold">P</span>
          </div>
          <div className="text-left">
            <h1 className="text-sm font-bold text-white">Pellago</h1>
            <span className="text-[10px] text-[#8b949e]">Property Management</span>
          </div>
        </button>
      </div>

      {/* User */}
      <div className="p-3">
        <div className="flex items-center gap-2.5 p-2.5 rounded-lg bg-[#161b22] border border-[#21262d]">
          <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-lg flex items-center justify-center">
            <span className="text-[#0d1117] font-semibold text-xs">JD</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-white truncate">John Doe</p>
            <p className="text-[10px] text-[#8b949e]">Admin</p>
          </div>
          <div className="relative">
            <div className="text-[#8b949e]">{icons.bell}</div>
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[9px] font-bold text-white flex items-center justify-center">3</span>
          </div>
        </div>
      </div>

      {/* Dashboard Link */}
      <div className="px-2.5 pb-2">
        <button
          onClick={() => onPageChange('dashboard')}
          className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-all w-full ${
            activePage === 'dashboard'
              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
              : 'text-[#8b949e] hover:bg-[#161b22] hover:text-white'
          }`}
        >
          <div className={activePage === 'dashboard' ? 'text-emerald-400' : 'text-[#6e7681]'}>
            {icons.home}
          </div>
          <span className="flex-1 text-left">Dashboard</span>
          {activePage === 'dashboard' && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2 px-2.5">
        {mockNavigation.map((section, idx) => (
          <div key={section.section} className={idx > 0 ? 'mt-4' : ''}>
            <div className="px-2.5 mb-1.5">
              <span className="text-[9px] font-semibold text-[#6e7681] uppercase tracking-wider">
                {section.section}
              </span>
            </div>
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const isActive = activePage === item.page;
                return (
                  <button
                    key={item.name}
                    onClick={() => onPageChange(item.page)}
                    className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-all w-full ${
                      isActive
                        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                        : 'text-[#8b949e] hover:bg-[#161b22] hover:text-white'
                    }`}
                  >
                    <div className={isActive ? 'text-emerald-400' : 'text-[#6e7681]'}>
                      {icons[item.icon]}
                    </div>
                    <span className="flex-1 text-left">{item.name}</span>
                    {item.badge && (
                      <span className="px-1.5 py-0.5 bg-orange-500/20 text-orange-400 text-[9px] font-bold rounded">
                        {item.badge}
                      </span>
                    )}
                    {isActive && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Version */}
      <div className="p-3 border-t border-[#21262d]">
        <p className="text-[9px] text-[#6e7681] text-center">v1.0 Demo</p>
      </div>
    </div>
  );
};

// ============================================
// Stats Card Component
// ============================================
interface StatsCardProps {
  title: string;
  value: string;
  change: string;
  trend: 'up' | 'down';
  icon: string;
}

const StatsCard: React.FC<StatsCardProps> = ({ title, value, change, trend, icon }) => {
  return (
    <div className="bg-[#161b22] border border-[#21262d] rounded-xl p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="w-7 h-7 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center justify-center text-emerald-400 font-bold text-xs">
          {icon}
        </span>
        <span className={`text-[10px] font-medium ${trend === 'up' ? 'text-emerald-400' : 'text-red-400'}`}>
          {change}
        </span>
      </div>
      <h3 className="text-[#8b949e] text-[10px] mb-0.5">{title}</h3>
      <p className="text-lg font-bold text-white">{value}</p>
    </div>
  );
};

// ============================================
// Page Content Components
// ============================================

// Dashboard Content
const DashboardContent: React.FC = () => (
  <div className="p-4 space-y-4">
    {/* Alerts */}
    <div className="grid grid-cols-3 gap-2">
      <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-2.5">
        <div className="flex items-center gap-1.5 mb-1.5">
          <span className="w-5 h-5 bg-orange-500/20 rounded flex items-center justify-center text-orange-400 text-[10px] font-bold">
            {mockAlerts.expiringLeases.length}
          </span>
          <h3 className="text-[10px] font-semibold text-orange-400">Leases Expiring</h3>
        </div>
        <div className="space-y-1">
          {mockAlerts.expiringLeases.slice(0, 2).map((item) => (
            <div key={item.id} className="text-[9px] text-orange-300">
              <span className="font-medium">{item.unitName}</span>
              <span className="ml-1 text-orange-400/70">({item.daysLeft}d)</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2.5">
        <div className="flex items-center gap-1.5 mb-1.5">
          <span className="w-5 h-5 bg-yellow-500/20 rounded flex items-center justify-center text-yellow-400 text-[10px] font-bold">
            {mockAlerts.noticeDeadlines.length}
          </span>
          <h3 className="text-[10px] font-semibold text-yellow-400">Notice Deadlines</h3>
        </div>
        <div className="space-y-1">
          {mockAlerts.noticeDeadlines.map((item) => (
            <div key={item.id} className="text-[9px] text-yellow-300">
              <span className="font-medium">{item.unitName}</span>
              <span className="ml-1 text-yellow-400/70">({item.daysLeft}d)</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2.5">
        <div className="flex items-center gap-1.5 mb-1.5">
          <span className="w-5 h-5 bg-red-500/20 rounded flex items-center justify-center text-red-400 text-[10px] font-bold">
            {mockAlerts.overdueInvoices.length}
          </span>
          <h3 className="text-[10px] font-semibold text-red-400">Overdue</h3>
        </div>
        <div className="space-y-1">
          {mockAlerts.overdueInvoices.slice(0, 2).map((item) => (
            <div key={item.id} className="text-[9px] text-red-300">
              <span className="font-medium">{item.invoiceNumber}</span>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Stats Grid */}
    <div className="grid grid-cols-4 gap-2">
      <StatsCard title="Total Revenue" value={formatIDR(mockStats.totalRevenue)} change={`${mockStats.occupancyRate}% occ`} trend="up" icon="Rp" />
      <StatsCard title="Properties" value={String(mockStats.totalUnits)} change={`${mockStats.occupiedUnits} occupied`} trend="up" icon="H" />
      <StatsCard title="Contracts" value={String(mockStats.contractsSigned)} change={`${mockStats.contractsPending} pending`} trend="up" icon="D" />
      <StatsCard title="Overdue" value={String(mockStats.overduePayments)} change="Action needed" trend="down" icon="!" />
    </div>

    {/* Financial Summary & Property Overview */}
    <div className="grid grid-cols-3 gap-3">
      <div className="col-span-2 bg-[#161b22] border border-[#21262d] rounded-xl p-3">
        <h2 className="text-xs font-semibold text-white mb-3">Financial Summary</h2>
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2.5">
            <p className="text-[10px] text-[#8b949e] mb-0.5">Total Revenue</p>
            <p className="text-base font-bold text-emerald-400">{formatIDR(mockStats.totalRevenue)}</p>
          </div>
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2.5">
            <p className="text-[10px] text-[#8b949e] mb-0.5">Total Expenses</p>
            <p className="text-base font-bold text-red-400">{formatIDR(mockStats.totalExpenses)}</p>
          </div>
          <div className="col-span-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2.5">
            <p className="text-[10px] text-[#8b949e] mb-0.5">Net Income</p>
            <p className="text-xl font-bold text-emerald-400">{formatIDR(mockStats.netIncome)}</p>
          </div>
        </div>
      </div>

      <div className="bg-[#161b22] border border-[#21262d] rounded-xl p-3">
        <h2 className="text-xs font-semibold text-white mb-3">Property Overview</h2>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-[#8b949e]">Total Units</span>
            <span className="text-xs font-semibold text-white">{mockStats.totalUnits}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-[#8b949e]">Occupied</span>
            <span className="text-xs font-semibold text-emerald-400">{mockStats.occupiedUnits}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-[#8b949e]">Vacant</span>
            <span className="text-xs font-semibold text-yellow-400">{mockStats.vacantUnits}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-[#8b949e]">Occupancy</span>
            <span className="text-xs font-semibold text-white">{mockStats.occupancyRate}%</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Maintenance Content
const MaintenanceContent: React.FC = () => (
  <div className="p-4 space-y-4">
    <div className="flex items-center justify-between">
      <h2 className="text-sm font-semibold text-white">Work Orders</h2>
      <button className="px-3 py-1.5 bg-emerald-500 text-[#0d1117] text-[10px] font-semibold rounded-lg">+ New Order</button>
    </div>
    <div className="space-y-2">
      {mockWorkOrders.map((order) => (
        <div key={order.id} className="bg-[#161b22] border border-[#21262d] rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-medium text-white">{order.title}</h3>
            <span className={`px-2 py-0.5 text-[9px] font-semibold rounded ${
              order.status === 'Completed' ? 'bg-emerald-500/20 text-emerald-400' :
              order.status === 'In Progress' ? 'bg-blue-500/20 text-blue-400' :
              'bg-yellow-500/20 text-yellow-400'
            }`}>{order.status}</span>
          </div>
          <div className="flex items-center gap-4 text-[10px] text-[#8b949e]">
            <span>Priority: <span className={order.priority === 'High' ? 'text-red-400' : order.priority === 'Medium' ? 'text-yellow-400' : 'text-[#8b949e]'}>{order.priority}</span></span>
            <span>Assignee: {order.assignee}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Tenants Content
const TenantsContent: React.FC = () => (
  <div className="p-4 space-y-4">
    <div className="flex items-center justify-between">
      <h2 className="text-sm font-semibold text-white">Tenants</h2>
      <button className="px-3 py-1.5 bg-emerald-500 text-[#0d1117] text-[10px] font-semibold rounded-lg">+ Add Tenant</button>
    </div>
    <div className="space-y-2">
      {mockTenants.map((tenant) => (
        <div key={tenant.id} className="bg-[#161b22] border border-[#21262d] rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-violet-400 to-violet-600 rounded-full flex items-center justify-center">
                <span className="text-white text-[10px] font-bold">{tenant.name.split(' ').map(n => n[0]).join('')}</span>
              </div>
              <div>
                <h3 className="text-xs font-medium text-white">{tenant.name}</h3>
                <p className="text-[10px] text-[#8b949e]">{tenant.unit}</p>
              </div>
            </div>
            <span className={`px-2 py-0.5 text-[9px] font-semibold rounded ${
              tenant.status === 'Active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'
            }`}>{tenant.status}</span>
          </div>
          <div className="flex items-center justify-between text-[10px] text-[#8b949e]">
            <span>{tenant.lease}</span>
            <span className="text-emerald-400 font-medium">{formatIDR(tenant.rent)}/mo</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Expenses Content
const ExpensesContent: React.FC = () => (
  <div className="p-4 space-y-4">
    <div className="flex items-center justify-between">
      <h2 className="text-sm font-semibold text-white">Expenses</h2>
      <button className="px-3 py-1.5 bg-emerald-500 text-[#0d1117] text-[10px] font-semibold rounded-lg">+ Add Expense</button>
    </div>
    <div className="grid grid-cols-3 gap-2 mb-4">
      <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-3 text-center">
        <p className="text-[10px] text-[#8b949e]">This Month</p>
        <p className="text-base font-bold text-red-400">{formatIDR(28700000)}</p>
      </div>
      <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-3 text-center">
        <p className="text-[10px] text-[#8b949e]">Last Month</p>
        <p className="text-base font-bold text-[#8b949e]">{formatIDR(32100000)}</p>
      </div>
      <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-3 text-center">
        <p className="text-[10px] text-[#8b949e]">Change</p>
        <p className="text-base font-bold text-emerald-400">-10.6%</p>
      </div>
    </div>
    <div className="space-y-2">
      {mockExpenses.map((expense) => (
        <div key={expense.id} className="bg-[#161b22] border border-[#21262d] rounded-lg p-3 flex items-center justify-between">
          <div>
            <h3 className="text-xs font-medium text-white">{expense.description}</h3>
            <p className="text-[10px] text-[#8b949e]">{expense.category} • {expense.date}</p>
          </div>
          <span className="text-xs font-semibold text-red-400">{formatIDR(expense.amount)}</span>
        </div>
      ))}
    </div>
  </div>
);

// Properties Content
const PropertiesContent: React.FC = () => (
  <div className="p-4 space-y-4">
    <div className="flex items-center justify-between">
      <h2 className="text-sm font-semibold text-white">Properties</h2>
      <button className="px-3 py-1.5 bg-emerald-500 text-[#0d1117] text-[10px] font-semibold rounded-lg">+ Add Property</button>
    </div>
    <div className="space-y-2">
      {mockProperties.map((property) => (
        <div key={property.id} className="bg-[#161b22] border border-[#21262d] rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-xs font-medium text-white">{property.name}</h3>
              <p className="text-[10px] text-[#8b949e]">{property.type}</p>
            </div>
            <span className={`px-2 py-0.5 text-[9px] font-semibold rounded ${
              property.status === 'Occupied' ? 'bg-emerald-500/20 text-emerald-400' :
              property.status === 'Vacant' ? 'bg-red-500/20 text-red-400' :
              'bg-yellow-500/20 text-yellow-400'
            }`}>{property.status}</span>
          </div>
          <div className="flex items-center justify-between text-[10px]">
            <span className="text-[#8b949e]">{property.tenant || 'No tenant'}</span>
            <span className="text-emerald-400 font-medium">{formatIDR(property.rent)}/mo</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Daily Pulse Content - Analytics Dashboard
const DailyPulseContent: React.FC = () => {
  const maxRevenue = Math.max(...mockPulseData.revenueByMonth.map(d => d.revenue));

  return (
    <div className="p-4 space-y-4">
      {/* Today's Metrics */}
      <div className="grid grid-cols-6 gap-2">
        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-2.5 text-center">
          <p className="text-[9px] text-[#8b949e]">Check-ins</p>
          <p className="text-lg font-bold text-emerald-400">{mockPulseData.todayMetrics.checkIns}</p>
        </div>
        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-2.5 text-center">
          <p className="text-[9px] text-[#8b949e]">Check-outs</p>
          <p className="text-lg font-bold text-yellow-400">{mockPulseData.todayMetrics.checkOuts}</p>
        </div>
        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-2.5 text-center">
          <p className="text-[9px] text-[#8b949e]">Orders Created</p>
          <p className="text-lg font-bold text-blue-400">{mockPulseData.todayMetrics.workOrdersCreated}</p>
        </div>
        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-2.5 text-center">
          <p className="text-[9px] text-[#8b949e]">Orders Done</p>
          <p className="text-lg font-bold text-emerald-400">{mockPulseData.todayMetrics.workOrdersCompleted}</p>
        </div>
        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-2.5 text-center">
          <p className="text-[9px] text-[#8b949e]">Payments</p>
          <p className="text-sm font-bold text-emerald-400">{formatIDR(mockPulseData.todayMetrics.paymentsReceived)}</p>
        </div>
        <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-2.5 text-center">
          <p className="text-[9px] text-[#8b949e]">Issues</p>
          <p className="text-lg font-bold text-red-400">{mockPulseData.todayMetrics.issuesReported}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* Revenue Chart */}
        <div className="bg-[#161b22] border border-[#21262d] rounded-xl p-3">
          <h3 className="text-xs font-semibold text-white mb-3">Monthly Revenue</h3>
          <div className="flex items-end gap-2 h-24">
            {mockPulseData.revenueByMonth.map((d) => (
              <div key={d.month} className="flex-1 flex flex-col items-center">
                <div
                  className="w-full bg-emerald-500/80 rounded-t"
                  style={{ height: `${(d.revenue / maxRevenue) * 80}px` }}
                />
                <span className="text-[8px] text-[#8b949e] mt-1">{d.month}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Maintenance by Category */}
        <div className="bg-[#161b22] border border-[#21262d] rounded-xl p-3">
          <h3 className="text-xs font-semibold text-white mb-3">Maintenance by Category</h3>
          <div className="space-y-2">
            {mockPulseData.maintenanceByCategory.map((item) => (
              <div key={item.category} className="flex items-center gap-2">
                <span className="text-[10px] text-[#8b949e] w-16">{item.category}</span>
                <div className="flex-1 h-3 bg-[#21262d] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500/60 rounded-full"
                    style={{ width: `${(item.count / 15) * 100}%` }}
                  />
                </div>
                <span className="text-[10px] text-white w-6 text-right">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Occupancy Trend */}
      <div className="bg-[#161b22] border border-[#21262d] rounded-xl p-3">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-white">7-Day Occupancy Trend</h3>
          <span className="text-xs font-semibold text-emerald-400">{mockPulseData.occupancyTrend[6]}%</span>
        </div>
        <div className="flex items-end gap-1 h-12">
          {mockPulseData.occupancyTrend.map((val, i) => (
            <div key={i} className="flex-1 flex flex-col items-center">
              <div
                className={`w-full rounded-t ${i === 6 ? 'bg-emerald-500' : 'bg-emerald-500/40'}`}
                style={{ height: `${((val - 80) / 10) * 40}px` }}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[8px] text-[#6e7681]">Mon</span>
          <span className="text-[8px] text-[#6e7681]">Sun</span>
        </div>
      </div>
    </div>
  );
};

// Inventory Content
const InventoryContent: React.FC = () => (
  <div className="p-4 space-y-4">
    <div className="flex items-center justify-between">
      <h2 className="text-sm font-semibold text-white">Inventory</h2>
      <button className="px-3 py-1.5 bg-emerald-500 text-[#0d1117] text-[10px] font-semibold rounded-lg">+ Add Item</button>
    </div>

    {/* Stats */}
    <div className="grid grid-cols-4 gap-2">
      <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-2.5 text-center">
        <p className="text-[9px] text-[#8b949e]">Total Items</p>
        <p className="text-base font-bold text-white">{mockInventoryStats.totalItems}</p>
      </div>
      <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-2.5 text-center">
        <p className="text-[9px] text-[#8b949e]">Total Value</p>
        <p className="text-xs font-bold text-emerald-400">{formatIDR(mockInventoryStats.totalValue)}</p>
      </div>
      <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-2.5 text-center">
        <p className="text-[9px] text-[#8b949e]">Needs Service</p>
        <p className="text-base font-bold text-yellow-400">{mockInventoryStats.needsService}</p>
      </div>
      <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-2.5 text-center">
        <p className="text-[9px] text-[#8b949e]">In Repair</p>
        <p className="text-base font-bold text-red-400">{mockInventoryStats.inRepair}</p>
      </div>
    </div>

    {/* Inventory Table */}
    <div className="bg-[#161b22] border border-[#21262d] rounded-xl overflow-hidden">
      <table className="w-full text-[10px]">
        <thead>
          <tr className="border-b border-[#21262d]">
            <th className="text-left text-[#8b949e] font-medium p-2.5">Item</th>
            <th className="text-left text-[#8b949e] font-medium p-2.5">Category</th>
            <th className="text-left text-[#8b949e] font-medium p-2.5">Location</th>
            <th className="text-left text-[#8b949e] font-medium p-2.5">Status</th>
            <th className="text-right text-[#8b949e] font-medium p-2.5">Value</th>
          </tr>
        </thead>
        <tbody>
          {mockInventory.map((item) => (
            <tr key={item.id} className="border-b border-[#21262d] last:border-0">
              <td className="p-2.5">
                <span className="text-white font-medium">{item.name}</span>
              </td>
              <td className="p-2.5 text-[#8b949e]">{item.category}</td>
              <td className="p-2.5 text-[#8b949e]">{item.location}</td>
              <td className="p-2.5">
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${
                  item.status === 'Good' ? 'bg-emerald-500/20 text-emerald-400' :
                  item.status === 'Needs Service' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-blue-500/20 text-blue-400'
                }`}>{item.status}</span>
              </td>
              <td className="p-2.5 text-right text-emerald-400 font-medium">{formatIDR(item.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Vendors Content
const VendorsContent: React.FC = () => (
  <div className="p-4 space-y-4">
    <div className="flex items-center justify-between">
      <h2 className="text-sm font-semibold text-white">Vendors</h2>
      <button className="px-3 py-1.5 bg-emerald-500 text-[#0d1117] text-[10px] font-semibold rounded-lg">+ Add Vendor</button>
    </div>

    <div className="space-y-2">
      {mockVendors.map((vendor) => (
        <div key={vendor.id} className="bg-[#161b22] border border-[#21262d] rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-lg flex items-center justify-center">
                <span className="text-[#0d1117] font-bold text-[10px]">{vendor.name.charAt(0)}</span>
              </div>
              <div>
                <h3 className="text-xs font-medium text-white">{vendor.name}</h3>
                <p className="text-[10px] text-[#8b949e]">{vendor.category}</p>
              </div>
            </div>
            <span className={`px-2 py-0.5 text-[9px] font-semibold rounded ${
              vendor.status === 'Active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'
            }`}>{vendor.status}</span>
          </div>
          <div className="flex items-center justify-between text-[10px]">
            <div className="flex items-center gap-3">
              <span className="text-[#8b949e]">{vendor.contact}</span>
              <span className="text-yellow-400">★ {vendor.rating}</span>
            </div>
            <span className="text-[#8b949e]">{vendor.jobsCompleted} jobs</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Map Content - Simulated Property Map
const MapContent: React.FC = () => (
  <div className="p-4 space-y-4 h-full flex flex-col">
    <div className="flex items-center justify-between">
      <h2 className="text-sm font-semibold text-white">Property Map</h2>
      <div className="flex items-center gap-2">
        <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-[9px] rounded">Occupied: {mockStats.occupiedUnits}</span>
        <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-[9px] rounded">Vacant: {mockStats.vacantUnits}</span>
      </div>
    </div>

    {/* Simulated Map */}
    <div className="flex-1 bg-[#161b22] border border-[#21262d] rounded-xl p-3 relative overflow-hidden min-h-[200px]">
      {/* Map background pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle, #21262d 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
      </div>

      {/* Map Labels */}
      <div className="absolute top-3 left-3 text-[10px] text-[#6e7681]">Bali, Indonesia</div>

      {/* Property Markers */}
      <div className="relative h-full">
        {/* Seminyak Area */}
        <div className="absolute" style={{ top: '25%', left: '20%' }}>
          <div className="relative">
            <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse" />
            <div className="absolute -top-5 left-4 whitespace-nowrap">
              <span className="text-[9px] text-white bg-[#0d1117]/80 px-1.5 py-0.5 rounded">Seminyak (3)</span>
            </div>
          </div>
        </div>

        {/* Canggu Area */}
        <div className="absolute" style={{ top: '15%', left: '45%' }}>
          <div className="relative">
            <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse" />
            <div className="absolute -top-5 left-4 whitespace-nowrap">
              <span className="text-[9px] text-white bg-[#0d1117]/80 px-1.5 py-0.5 rounded">Canggu (1)</span>
            </div>
          </div>
        </div>

        {/* Kuta Area */}
        <div className="absolute" style={{ top: '60%', left: '30%' }}>
          <div className="relative">
            <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse" />
            <div className="absolute -top-5 left-4 whitespace-nowrap">
              <span className="text-[9px] text-white bg-[#0d1117]/80 px-1.5 py-0.5 rounded">Kuta (1)</span>
            </div>
          </div>
        </div>

        {/* Ubud Area */}
        <div className="absolute" style={{ top: '35%', left: '70%' }}>
          <div className="relative">
            <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse" />
            <div className="absolute -top-5 left-4 whitespace-nowrap">
              <span className="text-[9px] text-white bg-[#0d1117]/80 px-1.5 py-0.5 rounded">Ubud (1)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="absolute bottom-3 right-3 bg-[#0d1117]/80 rounded-lg p-2">
        <div className="flex items-center gap-3 text-[9px]">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-emerald-500 rounded-full" />
            <span className="text-[#8b949e]">Occupied</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-yellow-500 rounded-full" />
            <span className="text-[#8b949e]">Vacant/Notice</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Schedule Content
const ScheduleContent: React.FC = () => {
  const eventTypeColors: Record<string, string> = {
    maintenance: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    checkin: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    checkout: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    inspection: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    meeting: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  };

  // Group events by date
  const eventsByDate = mockScheduleEvents.reduce((acc, event) => {
    if (!acc[event.date]) acc[event.date] = [];
    acc[event.date].push(event);
    return acc;
  }, {} as Record<string, typeof mockScheduleEvents>);

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-white">Schedule</h2>
        <button className="px-3 py-1.5 bg-emerald-500 text-[#0d1117] text-[10px] font-semibold rounded-lg">+ Add Event</button>
      </div>

      {/* Event Type Legend */}
      <div className="flex items-center gap-2 flex-wrap">
        {Object.entries({ maintenance: 'Maintenance', checkin: 'Check-in', checkout: 'Check-out', inspection: 'Inspection', meeting: 'Meeting' }).map(([type, label]) => (
          <span key={type} className={`px-2 py-0.5 text-[9px] rounded border ${eventTypeColors[type]}`}>{label}</span>
        ))}
      </div>

      {/* Calendar View */}
      <div className="space-y-3">
        {Object.entries(eventsByDate).map(([date, events]) => (
          <div key={date} className="bg-[#161b22] border border-[#21262d] rounded-xl p-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-white">{new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
              <span className="px-1.5 py-0.5 bg-[#21262d] text-[#8b949e] text-[9px] rounded">{events.length} events</span>
            </div>
            <div className="space-y-1.5">
              {events.map((event) => (
                <div key={event.id} className={`flex items-center gap-2 p-2 rounded-lg border ${eventTypeColors[event.type]}`}>
                  <span className="text-[10px] font-medium w-10">{event.time}</span>
                  <span className="text-[10px] flex-1">{event.title}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Activity Log Content
const ActivityContent: React.FC = () => {
  const activityTypeIcons: Record<string, React.ReactNode> = {
    maintenance: <span className="text-blue-400">{icons.maintenance}</span>,
    payment: <span className="text-emerald-400">{icons.expenses}</span>,
    tenant: <span className="text-purple-400">{icons.tenants}</span>,
    expense: <span className="text-red-400">{icons.expenses}</span>,
    vendor: <span className="text-orange-400">{icons.vendors}</span>,
    inventory: <span className="text-yellow-400">{icons.inventory}</span>,
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-white">Activity Log</h2>
        <div className="flex items-center gap-2">
          <select className="bg-[#161b22] border border-[#21262d] text-[10px] text-[#8b949e] rounded-lg px-2 py-1">
            <option>All Activities</option>
            <option>Maintenance</option>
            <option>Payments</option>
            <option>Tenants</option>
          </select>
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="space-y-1">
        {mockActivityLog.map((activity, index) => (
          <div key={activity.id} className="flex gap-3">
            {/* Timeline */}
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 bg-[#161b22] border border-[#21262d] rounded-lg flex items-center justify-center">
                {activityTypeIcons[activity.type]}
              </div>
              {index < mockActivityLog.length - 1 && (
                <div className="w-px h-full bg-[#21262d] my-1" />
              )}
            </div>

            {/* Content */}
            <div className="flex-1 pb-3">
              <div className="bg-[#161b22] border border-[#21262d] rounded-lg p-2.5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-semibold text-white">{activity.action}</span>
                  <span className="text-[9px] text-[#6e7681]">{activity.timestamp}</span>
                </div>
                <p className="text-[10px] text-[#8b949e]">{activity.description}</p>
                <p className="text-[9px] text-[#6e7681] mt-1">by {activity.user}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// Main Dashboard Demo Component
// ============================================
interface DemoContentProps {
  activePage: PageType;
}

const DemoContent: React.FC<DemoContentProps> = ({ activePage }) => {
  const pageConfig: Record<PageType, { title: string; badge: string }> = {
    dashboard: { title: 'Dashboard', badge: 'Overview' },
    maintenance: { title: 'Maintenance', badge: 'Work Orders' },
    expenses: { title: 'Expenses', badge: 'Tracking' },
    pulse: { title: 'Daily Pulse', badge: 'Analytics' },
    properties: { title: 'Properties', badge: 'Management' },
    tenants: { title: 'Tenants', badge: 'Records' },
    inventory: { title: 'Inventory', badge: 'Assets' },
    vendors: { title: 'Vendors', badge: 'Partners' },
    map: { title: 'Map', badge: 'Locations' },
    schedule: { title: 'Schedule', badge: 'Calendar' },
    activity: { title: 'Activity', badge: 'Log' },
  };

  const config = pageConfig[activePage];

  return (
    <div className="flex-1 overflow-y-auto flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[#21262d] bg-[#0d1117]">
        <div className="flex items-center gap-2">
          <h1 className="text-base font-bold text-white">{config.title}</h1>
          <span className="px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-medium rounded-full">
            {config.badge}
          </span>
        </div>
      </div>

      {/* Page Content */}
      <div className="flex-1 overflow-y-auto">
        {activePage === 'dashboard' && <DashboardContent />}
        {activePage === 'maintenance' && <MaintenanceContent />}
        {activePage === 'tenants' && <TenantsContent />}
        {activePage === 'expenses' && <ExpensesContent />}
        {activePage === 'properties' && <PropertiesContent />}
        {activePage === 'pulse' && <DailyPulseContent />}
        {activePage === 'inventory' && <InventoryContent />}
        {activePage === 'vendors' && <VendorsContent />}
        {activePage === 'map' && <MapContent />}
        {activePage === 'schedule' && <ScheduleContent />}
        {activePage === 'activity' && <ActivityContent />}
      </div>
    </div>
  );
};

// ============================================
// Interactive Demo Wrapper
// ============================================
export const PellagoDashboardDemo: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activePage, setActivePage] = useState<PageType>('dashboard');

  return (
    <div className="relative">
      {/* Demo Badge */}
      <div className="absolute top-3 right-3 z-20 flex items-center gap-2">
        <span className="px-2.5 py-1 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-xs font-medium rounded-full backdrop-blur-sm">
          Live Demo
        </span>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="px-2.5 py-1 bg-zinc-800/80 border border-zinc-700 text-zinc-300 text-xs font-medium rounded-full backdrop-blur-sm hover:bg-zinc-700/80 transition-colors"
        >
          {isExpanded ? 'Collapse' : 'Expand'}
        </button>
      </div>

      {/* Dashboard Preview */}
      <motion.div
        className="relative rounded-xl overflow-hidden border border-zinc-800 bg-[#0d1117] shadow-2xl"
        animate={{ height: isExpanded ? 600 : 400 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex h-full">
          {/* Sidebar */}
          <DemoSidebar activePage={activePage} onPageChange={setActivePage} />

          {/* Main Content */}
          <DemoContent activePage={activePage} />
        </div>

        {/* Demo Overlay Gradient */}
        {!isExpanded && (
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#0d1117] to-transparent pointer-events-none" />
        )}
      </motion.div>

      {/* Info Bar */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs text-zinc-400">Interactive Demo</span>
          </div>
          <span className="text-xs text-zinc-600">|</span>
          <span className="text-xs text-zinc-500">Click sidebar tabs to explore • No database connection</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <span>Built with</span>
          <span className="px-2 py-0.5 bg-zinc-800 rounded text-zinc-400">Next.js</span>
          <span className="px-2 py-0.5 bg-zinc-800 rounded text-zinc-400">React</span>
          <span className="px-2 py-0.5 bg-zinc-800 rounded text-zinc-400">Tailwind</span>
        </div>
      </div>
    </div>
  );
};

export default PellagoDashboardDemo;
